import { useForm } from "react-hook-form"
import { type IUpdateAccountStatusRequest } from "@apis/accounts/types"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Textarea } from "@components/ui/textarea"
import {
  useGetAccountStatusesQuery,
  useGetAccountStatusReasonsQuery,
  useGetRevenueClassesQuery,
} from "@apis/shared/useShared"
import { useParams } from "react-router"
import { useUpdateAccountStatusMutation } from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { useAgedAccountSerializer } from "@pages/AgedAccounts/utils/agedAccountSerializerSchema"
import { Loader2 } from "lucide-react"
import { useGetUtilityBillCyclesQuery } from "@apis/utilities/useUtilities"
import { useMemo } from "react"
import { DatePicker } from "@components/DatePicker"
import { Button } from "@components/ui/button"

export const AccountStatusForm = ({
  onFormSuccess,
}: {
  onFormSuccess?: () => void
}) => {
  const { account_id } = useParams() as { account_id: string }
  const updateAccountStatusMutation = useUpdateAccountStatusMutation()
  const queryClient = useQueryClient()

  const { data: agedAccountData, isPending } =
    useAgedAccountSerializer(account_id)
  const { data: accountStatuses } = useGetAccountStatusesQuery()
  const { data: accountStatusReasons } = useGetAccountStatusReasonsQuery()
  const { data: billCycles } = useGetUtilityBillCyclesQuery(
    agedAccountData?.utility_code,
  )
  const { data: revenueClassCodes } = useGetRevenueClassesQuery()

  const EMPTY_ACCOUNT_STATUS_FORM = {
    account_status_code: "",
    account_status_reason_code: "",
    bill_cycle: "",
    revenue_class_code: "",
    confirmed_start_dt: "",
    account_status_message: "",
    confirmed_drop_dt: "",
  }

  const serializeAccountStatusFormData = useMemo(() => {
    if (isPending) return undefined

    return {
      account_status_code: "",
      account_status_reason_code: "",
      account_status_message: "",
      bill_cycle: agedAccountData?.bill_cycle ?? "",
      revenue_class_code: agedAccountData?.revenue_class_code ?? "",
      confirmed_start_dt: agedAccountData?.confirmed_start_dt ?? "",
      confirmed_drop_dt: "",
    }
  }, [agedAccountData, isPending])

  const form = useForm<IUpdateAccountStatusRequest>({
    mode: "onTouched",
    defaultValues: EMPTY_ACCOUNT_STATUS_FORM,
    values: serializeAccountStatusFormData,
  })

  const handleSubmit = async (data: IUpdateAccountStatusRequest) => {
    updateAccountStatusMutation.mutate(
      {
        account_id: account_id,
        request: data,
      },
      {
        onSuccess: () => {
          toast.success("Successfully changed account state!")
          form.reset()
          queryClient.invalidateQueries({
            queryKey: accountKeys.agedAccounts(),
          })
          queryClient.invalidateQueries({
            queryKey: accountKeys.detail(account_id),
          })
          onFormSuccess?.()
        },
        onError: (error) =>
          toast.error(`Error changing account state! ${error}`),
      },
    )
  }

  const accountStatusCode = form.watch("account_status_code")

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
      >
        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="account_status_code"
            rules={{ required: "Account status is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Status</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value)
                    form.resetField("confirmed_start_dt")
                    form.resetField("confirmed_drop_dt")
                  }}
                  value={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Account Status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {accountStatuses?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="account_status_reason_code"
            rules={{ required: "Account status reason is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status Reason</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Reason" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {accountStatusReasons?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {accountStatusCode === "ACTIVE" && (
          <div className="tw:col-span-12 tw:grid tw:grid-cols-subgrid tw:gap-4">
            <div className="tw:col-span-12 tw:md:col-span-4">
              <FormField
                control={form.control}
                name="bill_cycle"
                rules={{ required: "Bill cycle is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bill Cycle</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select a Bill Cycle" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {billCycles?.map((billCycle) => (
                          <SelectItem key={billCycle} value={billCycle}>
                            {billCycle}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12 tw:md:col-span-4">
              <FormField
                control={form.control}
                name="revenue_class_code"
                rules={{ required: "Account status reason is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Revenue Class Code</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Revenue Class Code" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {revenueClassCodes?.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12 tw:md:col-span-4">
              <FormField
                control={form.control}
                name="confirmed_start_dt"
                rules={{ required: "Confirmed Start Date is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmed Start Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        field={field}
                        disabled={(date) => date > new Date()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        )}

        {accountStatusCode === "INACTIVE" && (
          <div className="tw:col-span-12 tw:grid tw:grid-cols-subgrid tw:gap-4">
            <div className="tw:col-span-12 tw:md:col-span-6">
              <FormField
                control={form.control}
                rules={{ required: "Confirmed Start Date is required" }}
                name="confirmed_start_dt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmed Start Date</FormLabel>
                    <FormControl>
                      <DatePicker field={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="tw:col-span-12 tw:md:col-span-6">
              <FormField
                control={form.control}
                rules={{ required: "Confirmed Drop Date is required" }}
                name="confirmed_drop_dt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmed Drop Date</FormLabel>
                    <FormControl>
                      <DatePicker field={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        )}

        <div className="tw:col-span-12">
          <FormField
            control={form.control}
            name="account_status_message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea
                    className="tw:field-sizing-content"
                    placeholder="Enter a detailed message about the status change"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:justify-self-end">
          <Button
            type="submit"
            disabled={
              updateAccountStatusMutation.isPending ||
              !form.formState.isDirty ||
              !form.formState.isValid
            }
            className="tw:flex-1"
          >
            {updateAccountStatusMutation.isPending ? (
              <>
                <Loader2 className="tw:animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <span>Update Status</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
